import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Commons
import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { Filters } from '@commons/Filters';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { IMPORT_MODAL_STEPS } from '@commons/Modals/MultiStepModal';
import { userHasRoleInpulse } from '@commons/utils/roles';
import EmptyState from '@commons/EmptyState';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import utilsXLS from '@commons/utils/makeXLS';

// Selectors
import { canRequestTransfer, canSendTransfer } from '@selectors/actions/transferActions';
import { getAuthorizedActions, getHasTransferDeliveryTime } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

// Services
import transferListService from '@services/transferList';

import { fetchGroupsByStoreIds } from '@orders/OrderAnalyticsModulesContainer/utils/fetches';

// Components
import { ComponentContainer } from './styledComponents';
import { ExportListModal } from './utils/exports/exportList';
import { FiltersContainer } from '../StocksInventoriesTransfer/styledComponents';

// Utils
import { downloadFile, formatXlsFileToJson } from '@backoffice/utils';
import {
  formatTransfersBatchCreationFile,
  getImportTransfersModalConfig,
} from './utils/batchCreation/import';
import { getActions, getGoToTransferActions, getRowActions } from './utils/getActions';
import { getColumns } from './utils/getColumns';
import {
  getRecipeTransfertsExportColumns,
  getSupplierProductTransfertsExportColumns,
} from '@stocks/StocksInventories/Transfers/utils/getExportContentColumns';

// Miscellaneous
import { BatchExportModal } from '@commons/BatchExportModal';
import { ConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatTimeWithLanguageCode, getUserTimezone } from '@commons/utils/date';
import { getCustomTransferListStoreFilter } from '@commons/utils/filtersFetches';
import {
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
  closeGenericModal,
} from '@actions/modal';
import { useFiltersButtonState } from '@hooks/useFiltersButtonState';

const FILTER_TYPES = ['date', 'string', 'numeric'];

const TRANSFER_LIST_STORE_FILTER_OPTIONS = () => [
  { id: 'sender', name: i18next.t('GENERAL.SENDER') },
  { id: 'recipient', name: i18next.t('GENERAL.RECIPIENT') },
];

export const Transfers = (props) => {
  const {
    match,
    history,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
    authorizedActions,
    user,
    client: { storeName },
    userSalesPointStores,
    clientBrands,
    clientRetailers,
    clientLocations,
    currency,
    openGenericModal,
    openModalExportInfo,
    hasTransferDeliveryTime,
    refreshGenericModal,
    closeGenericModal,
  } = props;

  const path = get(match, 'path');
  const clientStoreNameTranslation = getClientStoreNameTranslation(storeName);
  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');

  /**
   * STATES
   */
  const [transfers, setTransfers] = useState([]);
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);

  const [columns] = useState(
    getColumns({
      storeName: clientStoreNameTranslation,
      hasTransferDeliveryTime,
      userLanguageCode,
    }),
  );

  const [actions, setActions] = useState([]);
  const [rowActions, setRowActions] = useState([]);

  // Filters states
  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const filtersButtonState = useFiltersButtonState(advancedFilters);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedTransferListStoreFilter, setSelectedTransferListStoreFilter] = useState(
    TRANSFER_LIST_STORE_FILTER_OPTIONS(),
  );

  const columnsFilterList = columns.filter((column) => FILTER_TYPES.includes(column.filterType));

  const [filtersModalParams, setFilterModalParams] = useState({
    applyFilters,
    setApplyFilters,
    filters,
    setFilters,
    advancedFilters,
    setAdvancedFilters,
    selectedBrands,
    setSelectedBrands,
    selectedRetailers,
    setSelectedRetailers,
    selectedLocations,
    setSelectedLocations,
    selectedStores,
    setSelectedStores,
    groups,
    selectedGroups,
    setSelectedGroups,
    columnsFilterList,
    customMultipleDropDowns: [
      getCustomTransferListStoreFilter(
        TRANSFER_LIST_STORE_FILTER_OPTIONS(),
        selectedTransferListStoreFilter,
        setSelectedTransferListStoreFilter,
      ),
    ],
  });

  /**
   * FUNCTIONS
   */
  const fetchTransfers = async () => {
    setIsLoading(true);
    pageLoading();

    try {
      const transfers = await transferListService.getTransferLists();

      const formattedTransfers = transfers.map((transfer) => ({
        ...transfer,
        status: transfer.status.toString(),
        senderStoreName: transfer.senderStore.name, // required for a proper search via the column's propertyKey
        recipientStoreName: transfer.recipientStore.name,
      }));

      setTransfers(formattedTransfers);
      setFilteredTransfers(formattedTransfers);
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.FETCH_ERROR'));
      setTransfers([]);
      setFilteredTransfers([]);
    }

    setIsLoading(false);
    pageLoaded();
  };

  const getLocations = () => {
    const isInpulseUser = userHasRoleInpulse(user);

    if (!isInpulseUser) {
      setLocations([]);
      return [];
    }

    setLocations(clientLocations);

    return clientLocations;
  };

  const goToCreateTransfer = (isSender) => {
    history.push(`/stocks/inventories/transfers/create?isSender=${isSender}`);
  };

  const openExistingTransfer = (transferId) => {
    history.push(`/stocks/inventories/transfers/${transferId}/details`);
  };

  const initializeFilters = (fetchedGroups) => {
    const clientLocations = getLocations();

    const updatedFilters = {
      brands: !isEmpty(clientBrands)
        ? {
            list: [...clientBrands],
            selectedBrands,
          }
        : null,
      retailers: !isEmpty(clientRetailers)
        ? {
            list: [...clientRetailers],
            selectedRetailers,
          }
        : null,
      locations: !isEmpty(clientLocations)
        ? {
            list: [...clientLocations],
            selectedLocations,
          }
        : null,
      groups: !isEmpty(fetchedGroups)
        ? {
            list: [...fetchedGroups],
            selectedGroups,
          }
        : null,
      stores: {
        activeList: [...userSalesPointStores],
        list: [...userSalesPointStores],
        selectedStores: [...userSalesPointStores],
      },
    };

    setFilters(updatedFilters);
    setApplyFilters(true);
  };

  const applySelectedTransferTypes = (transfers) => {
    const transfersWithSelectedStores = transfers.filter(({ senderStore, recipientStore }) => {
      const hasSenderSelected = selectedTransferListStoreFilter.some(({ id }) => id === 'sender');
      const hasRecipientSelected = selectedTransferListStoreFilter.some(
        ({ id }) => id === 'recipient',
      );

      if (hasSenderSelected && !hasRecipientSelected) {
        return selectedStores.some(({ id }) => id === senderStore.id);
      }

      if (!hasSenderSelected && hasRecipientSelected) {
        return selectedStores.some(({ id }) => id === recipientStore.id);
      }

      return (
        selectedStores.some(({ id }) => id === senderStore.id) ||
        selectedStores.some(({ id }) => id === recipientStore.id)
      );
    });

    return transfersWithSelectedStores;
  };

  const setToInvoiced = async (transferLists) => {
    const transferListIds = transferLists.map(({ id }) => id);
    pageLoading();

    try {
      await transferListService.setToInvoiced(transferListIds);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const cancelInvoiced = async (transferLists) => {
    const transferListIds = transferLists.map(({ id }) => id);
    pageLoading();

    try {
      await transferListService.cancelInvoiced(transferListIds);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const setToDraft = async (transferLists) => {
    const transferListIds = transferLists.map(({ id }) => id);
    pageLoading();

    try {
      await transferListService.setToDraft(transferListIds);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const cancelReception = async (transferLists) => {
    const transferListIds = transferLists.map(({ id }) => id);
    pageLoading();

    try {
      await transferListService.cancelReception(transferListIds);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const cancelSend = async (transferLists) => {
    const transferListIds = transferLists.map(({ id }) => id);
    pageLoading();

    try {
      await transferListService.cancelSend(transferListIds);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const openDeleteConfirmationModal = (transferListId) => {
    const params = {
      type: 'warning',
      width: '542px',
      height: 'auto',
      icon: '/images/inpulse/warning-white-small.svg',
      title: i18next.t('STOCKS.TRANSFERS.DELETE_CONFIRMATION_MODAL_TITLE'),
      component: ConfirmationModal,
      data: {
        content: i18next.t('STOCKS.TRANSFERS.DELETE_CONFIRMATION_MODAL_CONTENT'),
      },
      actions: [
        {
          key: 0,
          color: 'inpulse-outline',
          label: i18next.t('GENERAL.CANCEL'),
          icon: '/images/inpulse/close-black-small.svg',
        },
        {
          key: 1,
          color: 'inpulse-default',
          label: i18next.t('GENERAL.VALIDATE'),
          icon: '/images/inpulse/check-white-small.svg',
          handleClick: () => {
            deleteTransfer(transferListId);
          },
        },
      ],
    };

    openGenericModal(params);
  };

  const deleteTransfer = async (transferListId) => {
    pageLoading();

    try {
      await transferListService.deleteTransfer(transferListId);

      await fetchTransfers();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DELETE_FAILURE'));
    } finally {
      pageLoaded();
    }
  };

  const exportList = async (selectedTransferLists) => {
    const transferListsToExport =
      selectedTransferLists.length > 0 ? selectedTransferLists : filteredTransfers;
    const transferListIds = transferListsToExport.map(({ id }) => id);

    pageLoading();

    try {
      const exportedTransferLists = await transferListService.exportTransferLists(transferListIds);

      const mainSheetData = exportedTransferLists.map((transferList) => ({
        reference: transferList.reference,
        sender: transferList.sender,
        recipient: transferList.recipient,
        deliveryTime: transferList.deliveryTime
          ? formatTimeWithLanguageCode(transferList.deliveryTime, userLanguageCode)
          : null,
        sentAt: moment(transferList.sentAt),
        status: transferList.status,
        totalTransferNote: transferList.totalTransferNote,
        totalDeliveryNote: transferList.totalDeliveryNote,
        totalReceived: transferList.totalReceived,
      }));

      openModalExportInfo({
        component: ExportListModal,
        title: i18next.t('STOCKS.TRANSFERS.EXPORT_MODAL_TITLE'),
        mainSheetData,
        clientStoreNameTranslation,
        hasTransferDeliveryTime,
        sheetName: i18next.t('FEATURE.OPERATIONS.TRANSFER'),
        currency,
      });
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EXPORT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const triggerExportContent = (transferLists) => {
    const transferListIds = !isEmpty(transferLists)
      ? transferLists.map(({ id }) => id)
      : filteredTransfers.map(({ id }) => id);

    openModalExportInfo({
      component: BatchExportModal,
      title: i18next.t('STOCKS.TRANSFERS.EXPORT_MODAL_TITLE'),
      itemIds: transferListIds,
      requestFn: transferListService.exportContent,
      /**
       * Append several batches of transfer list content
       * @param {object[]} accumulator
       * @param {{supplierProducts: object[]; recipes: object[]; }} fetchedData
       */
      appendFn: (accumulator, fetchedData) => [...accumulator, fetchedData],
      /**
       * @param {{supplierProducts: object[]; recipes: object[]; }[]} data - All data to be exported
       */
      callbackFn: (data) => {
        const supplierProductTransfertsColumns = getSupplierProductTransfertsExportColumns({
          storeName: clientStoreNameTranslation,
          hasTransferDeliveryTime,
          userLanguageCode,
        });

        const recipeTransfertsColumns = getRecipeTransfertsExportColumns({
          storeName: clientStoreNameTranslation,
          hasTransferDeliveryTime,
          userLanguageCode,
        });

        const supplierProducts = data.flatMap(({ supplierProducts }) =>
          supplierProducts.map((supplierProduct) => ({
            ...supplierProduct,
            currency: currency.alphabeticCode,
          })),
        );

        const recipes = data.flatMap(({ recipes }) =>
          recipes.map((recipe) => ({
            ...recipe,
            currency: currency.alphabeticCode,
          })),
        );

        const supplierProductSheet = utilsXLS.generateDefaultSheet(
          i18next.t('GENERAL.SUPPLIER_PRODUCT_PLURAL'),
          supplierProductTransfertsColumns,
          supplierProducts,
          currency,
        );

        const recipeSheet = utilsXLS.generateDefaultSheet(
          i18next.t('GENERAL.RECIPE_PLURAL'),
          recipeTransfertsColumns,
          recipes,
          currency,
        );

        const date = moment
          .tz(getUserTimezone())
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

        utilsXLS.makeXLS(i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_FILE_NAME', { date }), [
          supplierProductSheet,
          recipeSheet,
        ]);
      },
    });
  };

  const downloadTransferBatchCreationFileTemplate = async () => {
    pageLoading();

    try {
      const today = moment().format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

      const result = await transferListService.downloadBatchCreationFileTemplate(
        userSalesPointStores.map(({ id }) => id),
      );

      downloadFile(
        result,
        i18next.t('STOCKS.TRANSFERS.BATCH_CREATION_TEMPLATE_FILENAME', { date: today }),
        false,
        false,
      );
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DOWNLOAD_TEMPLATE_FOR_IMPORT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const openTransferBatchCreationFileImportModal = () => {
    const params = getImportTransfersModalConfig(
      IMPORT_MODAL_STEPS.SELECT_FILE,
      {
        updatedFile: null,
        selectedFile: null,
        handleFileChange: onImportTransfersFileChange,
      },
      props,
    );

    openGenericModal(params);
  };

  const onImportTransfersFileChange = (file) => {
    setSelectedFile(file);

    refreshGenericModal(
      getImportTransfersModalConfig(IMPORT_MODAL_STEPS.VALIDATE_FILE, {
        updatedFile: file,
        selectedFile,
        handleFileChange: onImportTransfersFileChange,
        handleFileValidation: handleTransfersBatchCreationFileValidation,
      }),
    );
  };

  const handleTransfersBatchCreationFileValidation = async (file) => {
    pageLoading();

    try {
      const transfersBatchCreationFile = await formatXlsFileToJson(file);

      const formattedTransfersBatchCreationFileData = formatTransfersBatchCreationFile(
        transfersBatchCreationFile,
      );

      await transferListService.validateBatchCreationFile(formattedTransfersBatchCreationFileData);

      refreshGenericModal(
        getImportTransfersModalConfig(IMPORT_MODAL_STEPS.VALIDATED_FILE, {
          updatedFile: file,
          handleFileImport: () =>
            handleBatchTransfersCreation(formattedTransfersBatchCreationFileData),
        }),
      );
    } catch (error) {
      if (!error.response) {
        showErrorMessage(i18next.t('STOCKS.TRANSFERS.BATCH_CREATION_FAILURE'));

        closeGenericModal();
        return;
      }

      refreshGenericModal(
        getImportTransfersModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
          updatedFile: file,
          errorFile: error.response.data,
          statusCode: error.response.status,
          handleFileChange: onImportTransfersFileChange,
          storeName: clientStoreNameTranslation,
          exportErrors: (data) => {
            downloadFile(data, 'error_transfers_batch_creation', true, false);
          },
        }),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleBatchTransfersCreation = async (payload) => {
    pageLoading();

    try {
      await transferListService.importBatchCreation(payload);
      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.BATCH_CREATION_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.BATCH_CREATION_FAILURE'));
    } finally {
      pageLoaded();
      closeGenericModal();
      fetchTransfers();
    }
  };

  /**
   * EFFECTS
   */

  useEffect(() => {
    if (isEmpty(userSalesPointStores)) {
      return;
    }

    (async function loadData() {
      const storeIds = userSalesPointStores.map(({ id }) => id);
      const fetchedGroups = await fetchGroupsByStoreIds(storeIds, showErrorMessage);

      setGroups(fetchedGroups);
      setSelectedStores(userSalesPointStores);

      initializeFilters(fetchedGroups);
    })();
  }, [userSalesPointStores.length]);

  useEffect(() => {
    const actions = getActions({
      goToCreateTransfer,
      authorizedActions,
      selectedTransferLists: selectedItems,
      userSalesPointStores,
      setToInvoiced,
      cancelInvoiced,
      setToDraft,
      cancelReception,
      cancelSend,
      openDeleteConfirmationModal,
      exportList,
      triggerExportContent,
      downloadTransferBatchCreationFileTemplate,
      openTransferBatchCreationFileImportModal,
    });
    setActions(actions);

    const rowActions = getRowActions({
      userSalesPointStores,
      authorizedActions,
      setToInvoiced,
      cancelInvoiced,
      setToDraft,
      cancelReception,
      cancelSend,
      openDeleteConfirmationModal,
      exportList,
      triggerExportContent,
    });
    setRowActions(rowActions);
  }, [selectedItems, userSalesPointStores]);

  useEffect(() => {
    if (!!applyFilters && selectedStores.length) {
      fetchTransfers();
    }
  }, [applyFilters]);

  useEffect(() => {
    setFilterModalParams({
      ...filtersModalParams,
      brands: clientBrands,
      retailers: clientRetailers,
      locations,
      groups,
      stores: userSalesPointStores,
      filters,
      customMultipleDropDowns: [
        getCustomTransferListStoreFilter(
          TRANSFER_LIST_STORE_FILTER_OPTIONS(),
          selectedTransferListStoreFilter,
          setSelectedTransferListStoreFilter,
        ),
      ],
    });
  }, [clientBrands, clientRetailers, locations, groups, userSalesPointStores, filters]);

  useEffect(() => {
    setFilterModalParams({
      ...filtersModalParams,
      selectedBrands,
      selectedRetailers,
      selectedLocations,
      selectedGroups,
      selectedStores,
      filters,
      customMultipleDropDowns: [
        getCustomTransferListStoreFilter(
          TRANSFER_LIST_STORE_FILTER_OPTIONS(),
          selectedTransferListStoreFilter,
          setSelectedTransferListStoreFilter,
        ),
      ],
    });
  }, [
    selectedBrands,
    selectedRetailers,
    selectedLocations,
    selectedGroups,
    selectedStores,
    filters,
  ]);

  useEffect(() => {
    setFilterModalParams({
      ...filtersModalParams,
      advancedFilters,
      customMultipleDropDowns: [
        getCustomTransferListStoreFilter(
          TRANSFER_LIST_STORE_FILTER_OPTIONS(),
          selectedTransferListStoreFilter,
          setSelectedTransferListStoreFilter,
        ),
      ],
    });
  }, [advancedFilters]);

  useEffect(() => {
    if (!applyFilters) {
      return;
    }
    const filteredTransfers = applySelectedTransferTypes(transfers);

    if (!advancedFilters || !advancedFilters.length) {
      setFilteredTransfers(filteredTransfers);
      return;
    }

    if (!isLoading) {
      const filteredTransferListWithAdvancedFilters = advancedFilters.reduce(
        (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
        filteredTransfers,
      );

      setFilteredTransfers(filteredTransferListWithAdvancedFilters);
    }
  }, [advancedFilters, applyFilters, isLoading, selectedTransferListStoreFilter]);

  /**
   * RENDER
   */
  if (!isLoading && transfers.length === 0) {
    return (
      <ComponentContainer>
        <NavigationBreadCrumb featurePath={path} />
        <EmptyState
          icon="/images/inpulse/supplier-ingredients.svg"
          label={i18next.t('STOCKS.TRANSFERS.GLOBAL_EMPTY_STATE_TITLE')}
          renderActionButton={() =>
            (canRequestTransfer(authorizedActions) || canSendTransfer(authorizedActions)) && (
              <Button
                actions={getGoToTransferActions(goToCreateTransfer, authorizedActions)}
                color={'inpulse-default'}
                icon={'/images/inpulse/add-white-small.svg'}
                label={i18next.t('GENERAL.CREATE')}
                hideActionsIcon
              />
            )
          }
          subtitle={i18next.t('STOCKS.TRANSFERS.GLOBAL_EMPTY_STATE_SUBTITLE')}
        />
      </ComponentContainer>
    );
  }

  return (
    <ComponentContainer>
      <NavigationBreadCrumb featurePath={path} />
      <ListView
        actionOnClick={(transfer) => openExistingTransfer(transfer.id)}
        actions={actions}
        columns={columns}
        data={filteredTransfers}
        defaultOrderBy={'sentAt'}
        defaultOrderType={'desc'}
        isLoading={isLoading}
        languageCode={userLanguageCode}
        padding={'20px 24px 40px 24px'}
        placeholderShape={i18next.t('GENERAL.SEARCH')}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <FiltersContainer>
            <Filters
              filtersButtonState={filtersButtonState}
              filtersModalParams={filtersModalParams}
              filterText={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
            />
          </FiltersContainer>
        )}
        rowActions={rowActions}
        setSelectedItems={setSelectedItems}
      />
    </ComponentContainer>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  userSalesPointStores: getSalesPointStores(state.baseReducer.activeStores),
  client: getClientInfo(state.baseReducer.user),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/stocks/inventories/transfers',
  ),
  clientBrands: state.baseReducer.brands,
  clientRetailers: state.baseReducer.retailers,
  clientLocations: state.baseReducer.locations,
  currency: state.baseReducer.currency,
  hasTransferDeliveryTime: getHasTransferDeliveryTime(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => dispatch(loading()),
  pageLoaded: () => dispatch(loadingSuccess()),
  showSuccessMessage: (message) => dispatch(showSuccessMessage(message)),
  showErrorMessage: (message) => dispatch(showErrorMessage(message)),
  openGenericModal: (params) => dispatch(openGenericModal(params)),
  openModalExportInfo: (params) => dispatch(openSmallModal(params)),
  refreshGenericModal: (params) => dispatch(refreshGenericModal(params)),
  closeGenericModal: (params) => dispatch(closeGenericModal(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Transfers);
